import type { Locale } from '@/types/locale'
import { computed, defineComponent, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import {
  getStorageItemWithExpiry,
  setStorageItemWithExpiry,
} from '@/helpers/localStorageHelpers'
import AuthLayout from '@/layouts/AuthLayout/AuthLayout.vue'
import LandingLayout from '@/layouts/LandingLayout/LandingLayout.vue'
import MainLayout from '@/layouts/MainLayout/MainLayout.vue'
import { useMainStore } from '@/store/main'

export default defineComponent({
  name: 'App',
  components: {
    MainLayout,
    AuthLayout,
    LandingLayout,
  },
  setup() {
    const route = useRoute()
    const mainStore = useMainStore()
    const { locale } = useI18n()

    const componentKey = computed(() => {
      return route.path
    })

    const layoutComponent = computed(() => `${route.meta.layout}Layout`)

    const handleSetupLocaleLanguage = () => {
      const storageLocale = getStorageItemWithExpiry<Locale>('locale')
      if (storageLocale) {
        locale.value = storageLocale
        mainStore.$patch({
          appLocale: storageLocale,
        })
      }
      else {
        setStorageItemWithExpiry('locale', locale.value)
        mainStore.$patch({
          appLocale: locale.value as Locale,
        })
      }
    }

    watch(() => route.meta.layout, (layout) => {
      if (layout === 'Landing') {
        document.body.style.background = '#FFF'
        document.body.style.fontFamily = '\'Inter\', system-ui, -apple-system, sans-serif'
      }
      else {
        document.body.style.background = '#FAFAFA'
        document.body.style.fontFamily = 'Inter Tight'
      }
    }, { immediate: true })

    const setAppWindowWidth = () => {
      mainStore.$patch({
        windowWidth: window.innerWidth,
      })
    }

    const calculateDeviceUnitHeight = () => {
      if (mainStore.isMobile) {
        const vh = window.innerHeight * 0.01
        document.documentElement.style.setProperty('--vh', `${vh}px`)
      }
    }

    onMounted(async () => {
      handleSetupLocaleLanguage()
      setAppWindowWidth()
      calculateDeviceUnitHeight()
      window.addEventListener('scroll', calculateDeviceUnitHeight)
      window.addEventListener('resize', calculateDeviceUnitHeight)
      window.addEventListener('resize', setAppWindowWidth)
    })

    onUnmounted(() => {
      window.removeEventListener('scroll', calculateDeviceUnitHeight)
      window.removeEventListener('resize', calculateDeviceUnitHeight)
      window.removeEventListener('resize', setAppWindowWidth)
    })
    return {
      layoutComponent,
      mainStore,
      componentKey,
    }
  },
})
